import React, { useEffect, useState, useRef } from "react";
import Moment from "react-moment";
import GenerateExcel from "../../../../../components/GenerateExcel";
import SearchableSelect from "../../../../../comman/SearchableSelect";
import { formatAmount, getAllMonths, getAllYears } from "../../../../../services/appService";
import { useDispatch, useSelector } from "react-redux";
import { getMonthlyPaySubcontractorCandidates, loadMonthlyPaySubcontractorCandidates } from "../../../../store/reports";
import { getSubcontractorCandidates, getSubcontractorPartners, loadSubcontractorCandidates, loadSubcontractorPartners } from "../../../../store/partner";
import GeneratePdf from "../../../../../components/GeneratePdf";

const SubcontractorCandidates = () => {

    const years = getAllYears();
    const months = getAllMonths();

    const [year, setYear] = useState("");
    const [month, setMonth] = useState("");

    const [pdfShowHide, setPdfShowHide] = useState(false);
    const [excelShowHide, setExcelShowHide] = useState(false);
    const [selectedCol, setSelectedCol] = useState([]);
    const [selectedColData, setSelectedColData] = useState([]);

    const [partnerType, setPartnerType] = useState("");
    const [subcontractors, setSubcontractors] = useState([]);
    const [candidates, setCandidates] = useState([]);
    const [candidatePayRpt, setCandidatePayRpt] = useState([]);
    const [selectedCandidate, setSelectedCandidate] = useState(0);
    const [selectedSubcontractor, setSelectedSubcontractor] = useState("0");
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    // Live search, copy tip, and scroll states
    const [tableSearchTerm, setTableSearchTerm] = useState("");
    const [tableWidth, setTableWidth] = useState(0);
    const [isScrollable, setIsScrollable] = useState(false);
    const topScrollRef = useRef(null);
    const tableWrapperRef = useRef(null);

    const subcontractorPartnersSelector = useSelector(getSubcontractorPartners);
    const subcontractorCandidatesSelector = useSelector(getSubcontractorCandidates);
    const monthlyPaySubcontractorCandidatesSelector = useSelector(getMonthlyPaySubcontractorCandidates);

    const dispatch = useDispatch();

    useEffect(() => {
        if (subcontractorPartnersSelector.length > 0)
            setSubcontractors(subcontractorPartnersSelector);
    }, [subcontractorPartnersSelector]);

    const handlePartnerTypeSelect = (val) => {
        setPartnerType(val);
        setSubcontractors([]);
        setCandidates([]);
        setCandidatePayRpt([]);
        setSelectedCandidate(0);
        setSelectedSubcontractor("0");
        setYear("");
        setTableSearchTerm("");
        setHasFetched(false);
        dispatch(loadSubcontractorPartners(val));
    };

    const hanldeSubcontractorChange = async (value) => {
        let ptType = "";
        if (partnerType === "Recruiter")
            ptType = "recruiter";
        else if (partnerType === "Sub-Contractors")
            ptType = "subcontractor";

        dispatch(loadSubcontractorCandidates(value, ptType));
        setCandidatePayRpt([]);
        setSelectedCandidate(0);
        setSelectedSubcontractor(value || "0");
        setTableSearchTerm("");
        setHasFetched(false);
    };

    const handleCandidateChange = async (value) => {
        setSelectedCandidate(value || "0");
        setCandidatePayRpt([]);
        setTableSearchTerm("");
        setHasFetched(false);
    };

    useEffect(() => {
        setCandidates(subcontractorCandidatesSelector);
    }, [subcontractorCandidatesSelector]);

    useEffect(() => {
        if (hasFetched) {
            setCandidatePayRpt(monthlyPaySubcontractorCandidatesSelector || []);
        }
    }, [monthlyPaySubcontractorCandidatesSelector, hasFetched]);

    // Only settle loading when fresh API data arrives (not when hasFetched flips true)
    useEffect(() => {
        setIsLoading(false);
    }, [monthlyPaySubcontractorCandidatesSelector]);

    const handleYearChange = (value) => {
        setYear(value);
    };

    const handleGenerateRpt = () => {
        if (selectedCandidate) {
            setIsLoading(true);
            setHasFetched(true);
            let ptType = "";
            if (partnerType === "Recruiter")
                ptType = "recruiter";
            else if (partnerType === "Sub-Contractors")
                ptType = "subcontractor";

            dispatch(loadMonthlyPaySubcontractorCandidates(selectedCandidate, ptType));
            setTableSearchTerm("");
        }
    };

    const getFilteredReports = () => {
        if (!candidatePayRpt) return [];
        return candidatePayRpt.filter(item => {
            if (year !== "" && item.year !== parseInt(year)) {
                return false;
            }
            
            const term = tableSearchTerm.toLowerCase();
            const yearVal = String(item.year || "").toLowerCase();
            const monthName = (months.find((x) => x.id === item.month)?.name || "").toLowerCase();
            const invoiceNo = (item.invoiceNo || "").toLowerCase();
            const payMode = (item.paymentMode || "").toLowerCase();
            
            return yearVal.includes(term) || 
                   monthName.includes(term) || 
                   invoiceNo.includes(term) ||
                   payMode.includes(term);
        });
    };

    const filteredReports = getFilteredReports();

    // Stats calculations
    const getKpiStats = () => {
        if (!filteredReports || filteredReports.length === 0) {
            return { totalInvoiced: "$0.00", totalPaid: "$0.00", totalDue: "$0.00", totalHours: 0 };
        }
        let totalInv = 0;
        let totalPaid = 0;
        let totalHours = 0;
        
        filteredReports.forEach((item) => {
            totalInv += parseFloat(item.totalAmount) || 0;
            totalPaid += parseFloat(item.paidAmount) || 0;
            const hrs = item.hours || 0;
            const mins = item.minutes || 0;
            totalHours += hrs + (mins / 60);
        });
        
        return {
            totalInvoiced: "$" + formatAmount(totalInv),
            totalPaid: "$" + formatAmount(totalPaid),
            totalDue: "$" + formatAmount(totalInv - totalPaid),
            totalHours: Math.round(totalHours * 100) / 100
        };
    };

    const { totalInvoiced, totalPaid, totalDue, totalHours } = getKpiStats();

    const getSubconMonPayTotal = () => {
        let amountT = 0;
        let paidT = 0;
        let dueT = 0;

        filteredReports.forEach((item) => {
            const total = parseFloat(item.totalAmount) || 0;
            const paid = parseFloat(item.paidAmount) || 0;

            amountT += total;
            paidT += paid;
            dueT += total - paid;
        });

        return (
            <tr className="ach-pay-total-row">
                <td colSpan={3}></td>
                <td className="font-weight-bold text-right ach-pay-total-label">Total</td>

                {/* Total Amount */}
                <td className="font-weight-bold text-right ach-pay-total-value">
                    {"$" + formatAmount(amountT)}
                </td>

                <td colSpan={2}></td>

                {/* Total Paid */}
                <td className="font-weight-bold text-right ach-pay-total-value">
                    {"$" + formatAmount(paidT)}
                </td>

                {/* Total Due */}
                <td className="font-weight-bold text-right ach-pay-total-value">
                    {"$" + formatAmount(dueT)}
                </td>

                <td></td>
            </tr>
        );
    };

    const subcontractorPayExportPDF = () => {
        const headers = [
            [
                "Period",
                "Hours Worked",
                "Rate/hour",
                "Invoice Amount",
                "Invoice #",
                "Paid by ACH / CHECK",
                "Paid Amount",
                "Due Amount",
                "Date Paid",
            ],
        ];

        const colData = [];
        filteredReports.map((item) => {
            const dt = [];
            dt.push(months.find((x) => x.id === item.month)?.name || "");
            dt.push(
                `${item.hours || 0}h ${item.minutes || 0}m`,
                "$" + formatAmount(item.rate),
                item.totalAmount ? "$" + formatAmount(item.totalAmount) : "$0.00",
                item.invoiceNo,
                item.paymentMode,
                item.paidAmount ? "$" + formatAmount(item.paidAmount) : "$0.00",
                "$" + formatAmount((parseFloat(item.totalAmount) || 0) - (parseFloat(item.paidAmount) || 0)),
                item.paymentDate ? new Date(item.paymentDate).toLocaleDateString() : ""
            );
            colData.push(dt);
        });

        setSelectedCol(headers);
        setSelectedColData(colData);
        setPdfShowHide(true);
    }

    const subcontractorPayExportExcel = () => {
        const headers = [
            [
                "Period",
                "Hours Worked",
                "Rate/hour",
                "Invoice Amount",
                "Invoice #",
                "Paid by ACH / CHECK",
                "Paid Amount",
                "Due Amount",
                "Date Paid",
            ],
        ];

        const colData = [];
        filteredReports.forEach((item) => {
            const dt = [];
            dt.push(months.find((x) => x.id === item.month)?.name || "");
            dt.push(
                `${item.hours || 0}h ${item.minutes || 0}m`,
                "$" + formatAmount(item.rate),
                item.totalAmount ? "$" + formatAmount(item.totalAmount) : "$0.00",
                item.invoiceNo,
                item.paymentMode,
                item.paidAmount ? "$" + formatAmount(item.paidAmount) : "$0.00",
                "$" + formatAmount((parseFloat(item.totalAmount) || 0) - (parseFloat(item.paidAmount) || 0)),
                item.paymentDate ? new Date(item.paymentDate).toLocaleDateString() : ""
            );
            colData.push(dt);
        });

        setSelectedCol(headers);
        setSelectedColData(colData);
        setExcelShowHide(true);
    };

    const handlepdfShowHide = () => {
        setPdfShowHide(false);
        setSelectedCol([]);
        setSelectedColData([]);
    };

    const handleexcelShowHide = () => {
        setExcelShowHide(false);
        setSelectedCol([]);
        setSelectedColData([]);
    };

    // Dual scrollbars synchronization
    const handleTopScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            tableWrapperRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
    };

    const handleTableScroll = () => {
        if (topScrollRef.current && tableWrapperRef.current) {
            topScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
        }
    };

    useEffect(() => {
        const updateWidth = () => {
            const table = document.getElementById("sub_mon_pay");
            const wrapper = tableWrapperRef.current;
            if (table && wrapper) {
                setTableWidth(table.scrollWidth);
                setIsScrollable(table.scrollWidth > wrapper.clientWidth + 5);
            } else {
                setIsScrollable(false);
            }
        };
        
        updateWidth();
        const timer = setTimeout(updateWidth, 300);
        
        window.addEventListener("resize", updateWidth);
        return () => {
            window.removeEventListener("resize", updateWidth);
            clearTimeout(timer);
        };
    }, [filteredReports, partnerType]);

    return (
        <div className="ach-pay-container">
            <style>{`
                .ach-pay-container {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    color: #171717;
                    margin-bottom: 2rem;
                }

                /* Visual selector cards */
                .ach-pay-card-selector {
                    margin-bottom: 12px;
                }

                .ach-pay-select-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 14px;
                    padding: 13px 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
                    height: 100%;
                    position: relative;
                }

                .ach-pay-select-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px -4px rgba(15, 23, 42, 0.06);
                    border-color: #cbd5e1;
                }

                .ach-pay-select-card.active {
                    background: #ffffff !important;
                    border: 1.5px solid #171717 !important;
                    box-shadow: 0 6px 18px -4px rgba(15, 23, 42, 0.08), inset 4px 0 0 0 #171717 !important;
                }

                .ach-pay-select-card.active .ach-pay-select-card-title {
                    color: #171717 !important;
                    font-weight: 700 !important;
                }

                .ach-pay-select-card.active .ach-pay-select-card-desc {
                    color: #525252 !important;
                }

                .ach-pay-select-card-icon {
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 16px;
                    flex-shrink: 0;
                    background: #f5f5f5;
                    color: #171717;
                    border: 1px solid #d4d4d4;
                    transition: all 0.2s ease;
                }

                .ach-pay-select-card.active .ach-pay-select-card-icon {
                    background: #171717 !important;
                    color: #ffffff !important;
                    border-color: #171717 !important;
                }

                .ach-pay-card-check-badge {
                    position: absolute;
                    top: 10px;
                    right: 10px;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: #171717;
                    color: #ffffff;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 9.5px;
                    animation: badgePop 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }

                @keyframes badgePop {
                    from { transform: scale(0.6); opacity: 0; }
                    to   { transform: scale(1); opacity: 1; }
                }

                .ach-pay-select-card:hover .ach-pay-select-card-icon {
                    transform: scale(1.05);
                }

                .ach-pay-select-card-title {
                    font-size: 13.5px;
                    font-weight: 600;
                    color: #171717;
                    margin-bottom: 2px;
                }

                .ach-pay-select-card-desc {
                    font-size: 11px;
                    color: #64748b;
                    line-height: 1.3;
                }

                /* KPI Metric cards */
                .ach-pay-kpi-row {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 14px;
                    margin-bottom: 12px;
                }

                .ach-pay-metric-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px 16px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.01);
                }

                .ach-pay-metric-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 15px;
                    margin-right: 12px;
                    background: #f5f5f5;
                    color: #171717;
                    border: 1px solid #d4d4d4;
                    flex-shrink: 0;
                }

                .ach-pay-metric-val {
                    font-size: 17px;
                    font-weight: 600;
                    color: #171717;
                    line-height: 1.1;
                    margin-bottom: 2px;
                }

                .ach-pay-metric-label {
                    font-size: 10.5px;
                    color: #64748b;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                }

                /* Controls Layout */
                .ach-pay-controls {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 12px;
                    background-color: #fafbfc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 12px 16px;
                }

                .ach-pay-controls-group {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex-wrap: wrap;
                }

                .ach-pay-search-wrapper {
                    position: relative;
                    width: 240px;
                }

                .ach-pay-search-input {
                    width: 100%;
                    padding: 8px 12px 8px 34px;
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    font-size: 12.5px;
                    color: #171717;
                    transition: all 0.2s ease;
                }

                .ach-pay-search-input:focus {
                    outline: none;
                    border-color: #171717;
                    box-shadow: 0 0 0 3px rgba(23, 23, 23, 0.1);
                }

                .ach-pay-search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                    font-size: 12px;
                }

                .ach-pay-filter-select {
                    height: 35px;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    padding: 0 10px;
                    font-size: 12.5px;
                    color: #171717;
                    background: #ffffff;
                }

                .ach-pay-filter-select:focus {
                    outline: none;
                    border-color: #171717;
                }

                .ach-pay-actions-btns {
                    display: flex;
                    align-items: center;
                }

                .ach-pay-actions-btns > * {
                    margin-right: 8px !important;
                }

                .ach-pay-actions-btns > *:last-child {
                    margin-right: 0 !important;
                }

                .ach-pay-btn {
                    height: 35px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 6px;
                    padding: 0 14px;
                    border-radius: 8px;
                    font-size: 12.5px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.15s ease;
                    border: 1px solid #cbd5e1;
                    background: #ffffff;
                    color: #171717;
                }

                .ach-pay-btn:hover:not(:disabled) {
                    background-color: #f5f5f5;
                    color: #000000;
                    border-color: #94a3b8;
                }

                .ach-pay-btn:disabled {
                    background-color: #f1f5f9;
                    border-color: #e2e8f0;
                    color: #94a3b8;
                    cursor: not-allowed;
                }

                .ach-pay-btn-primary {
                    background: #171717 !important;
                    color: #ffffff !important;
                    border: 1px solid #171717 !important;
                    box-shadow: none !important;
                    transition: all 0.15s ease;
                    padding: 0 16px;
                }

                .ach-pay-btn-primary:hover:not(:disabled) {
                    background: #000000 !important;
                    border-color: #000000 !important;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                }

                .ach-pay-btn-primary:disabled {
                    background: #f1f5f9 !important;
                    border-color: #e2e8f0 !important;
                    color: #94a3b8 !important;
                    box-shadow: none !important;
                    cursor: not-allowed;
                }

                /* Table modifications matching WorkflowUser */
                .ach-pay-table-wrapper {
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    overflow-x: auto;
                    overflow-y: hidden;
                    background: white;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.01);
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar {
                    height: 6px !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-track {
                    background: #f1f5f9 !important;
                    border-radius: 10px !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #94a3b8, #cbd5e1) !important;
                    border-radius: 20px !important;
                }

                .ach-top-scrollbar-wrap::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #64748b, #94a3b8) !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar,
                .ach-pay-report-container div::-webkit-scrollbar {
                    width: 6px !important;
                    height: 6px !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-track,
                .ach-pay-report-container div::-webkit-scrollbar-track {
                    background: transparent !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb,
                .ach-pay-report-container div::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, #94a3b8, #cbd5e1) !important;
                    border-radius: 20px !important;
                    transition: all 0.2s ease !important;
                }

                .ach-pay-table-wrapper::-webkit-scrollbar-thumb:hover,
                .ach-pay-report-container div::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, #64748b, #94a3b8) !important;
                }

                .ach-pay-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                    text-align: left;
                }

                .ach-pay-table thead {
                    background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
                }

                .ach-pay-table thead tr {
                    background: transparent !important;
                }

                .ach-pay-table th {
                    background: transparent !important;
                    background-color: transparent !important;
                    color: #475569 !important;
                    font-weight: 600 !important;
                    font-size: 11.5px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.6px !important;
                    padding: 13px 16px !important;
                    border-bottom: 2px solid #e2e8f0 !important;
                    border-top: none !important;
                    border-left: none !important;
                    border-right: none !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr {
                    transition: background 0.15s ease, box-shadow 0.15s ease !important;
                }

                .ach-pay-table tbody tr:hover {
                    background: linear-gradient(90deg, #f5f5f5 0%, #fafafa 100%) !important;
                    box-shadow: inset 3px 0 0 0 #171717 !important;
                }

                .ach-pay-table tbody tr:hover td {
                    color: #000000 !important;
                }

                .ach-pay-table td {
                    padding: 13px 16px !important;
                    font-size: 13px !important;
                    color: #171717 !important;
                    border-bottom: 1px solid #f1f5f9 !important;
                    vertical-align: middle !important;
                    transition: color 0.15s ease !important;
                    white-space: nowrap !important;
                }

                .ach-pay-table tbody tr:last-child td {
                    border-bottom: none !important;
                }

                /* Column alignments and highlights */
                .ach-pay-table td.col-rate {
                    color: #475569 !important;
                    font-weight: 500;
                    text-align: right !important;
                }

                .ach-pay-table td.col-amount {
                    color: #171717 !important;
                    font-weight: 600;
                    background-color: rgba(245, 245, 245, 0.5);
                    text-align: right !important;
                }

                .col-monthly-val, .col-quarterly-val {
                    color: #475569 !important;
                    font-weight: 500;
                    text-align: right !important;
                }

                /* Table Cell Numeric Input Groups */
                .ach-cell-input-group {
                    width: 110px !important;
                    height: 30px !important;
                    border: 1px solid #cbd5e1 !important;
                    border-radius: 6px !important;
                    background: #ffffff !important;
                    display: flex !important;
                    align-items: center !important;
                    transition: all 0.15s ease !important;
                    overflow: hidden !important;
                }

                .ach-cell-input-group:focus-within {
                    border-color: #171717 !important;
                    box-shadow: 0 0 0 3px rgba(23, 23, 23, 0.1) !important;
                }

                .ach-cell-input-prefix {
                    background: transparent !important;
                    border: none !important;
                    color: #64748b !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    padding: 0 0 0 8px !important;
                    display: flex !important;
                    align-items: center !important;
                }

                .ach-cell-num-input {
                    border: none !important;
                    background: transparent !important;
                    box-shadow: none !important;
                    color: #171717 !important;
                    font-weight: 600 !important;
                    font-size: 12.5px !important;
                    height: 28px !important;
                    padding: 0 8px 0 2px !important;
                    text-align: right !important;
                    width: 100% !important;
                }

                .ach-cell-num-input:focus {
                    outline: none !important;
                    box-shadow: none !important;
                    border: none !important;
                }

                .ach-cell-num-input::-webkit-inner-spin-button,
                .ach-cell-num-input::-webkit-outer-spin-button,
                .ach-pay-table input[type="number"]::-webkit-inner-spin-button,
                .ach-pay-table input[type="number"]::-webkit-outer-spin-button {
                    -webkit-appearance: none !important;
                    margin: 0 !important;
                }

                .ach-cell-num-input[type="number"],
                .ach-pay-table input[type="number"] {
                    -moz-appearance: textfield !important;
                }

                /* Total Payment row layout */
                .ach-pay-total-row {
                    background-color: #f8fafc !important;
                    border-top: 2px solid #cbd5e1;
                    border-bottom: 2px solid #cbd5e1;
                }

                .ach-pay-total-row td {
                    color: #171717 !important;
                    font-weight: 700 !important;
                    padding: 14px 16px !important;
                }

                .ach-pay-total-label {
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    font-size: 11px;
                    color: #475569 !important;
                    text-align: right !important;
                }

                .ach-pay-total-value {
                    font-size: 13px;
                    color: #171717 !important;
                    font-weight: 800 !important;
                    background-color: #f8fafc !important;
                    text-align: right !important;
                }

                /* Copy button */
                .ach-copy-btn {
                    margin-left: 8px;
                    color: #94a3b8;
                    cursor: pointer;
                    font-size: 11px;
                    transition: color 0.15s ease;
                }

                .ach-copy-btn:hover {
                    color: #171717;
                }

                /* Shimmer and Loading Animation */
                @keyframes shimmer {
                  0%   { background-position: -600px 0; }
                  100% { background-position: 600px 0; }
                }

                @keyframes loadingBar {
                  0%   { width: 0%; opacity: 1; }
                  70%  { width: 85%; opacity: 1; }
                  100% { width: 100%; opacity: 0; }
                }

                .wf-loading-bar {
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  height: 3px;
                  background: linear-gradient(90deg, #171717, #525252, #171717);
                  background-size: 200% 100%;
                  animation: loadingBar 1.6s ease-in-out infinite;
                  z-index: 10;
                }

                .skeleton-cell {
                  display: block;
                  height: 12px;
                  border-radius: 6px;
                  background: linear-gradient(
                    90deg,
                    #f1f5f9 25%,
                    #e2e8f0 50%,
                    #f1f5f9 75%
                  );
                  background-size: 600px 100%;
                  animation: shimmer 1.4s ease-in-out infinite;
                }

                .skeleton-cell.sk-short  { width: 40%; }
                .skeleton-cell.sk-medium { width: 65%; }
                .skeleton-cell.sk-long   { width: 85%; }

                .skeleton-row td {
                  padding: 14px 16px !important;
                }

                /* Enhanced empty state */
                .wf-empty-state {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 50px 24px;
                  text-align: center;
                  gap: 12px;
                }

                .wf-empty-icon {
                  width: 52px;
                  height: 52px;
                  border-radius: 14px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 22px;
                  background: #f5f5f5;
                  border: 1px solid #d4d4d4;
                  color: #171717;
                  margin-bottom: 4px;
                }

                .wf-empty-title {
                  font-size: 15px;
                  font-weight: 600;
                  color: #171717;
                  margin: 0;
                  width: 100%;
                  text-align: center;
                }

                .wf-empty-desc {
                  font-size: 13px;
                  color: #64748b;
                  margin: 0 auto;
                  line-height: 1.6;
                  text-align: center;
                  width: 100%;
                }

                .ach-pay-loading-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 12.5px;
                    color: #64748b;
                    margin-right: 10px;
                }

                .ach-pay-loading-status i {
                    color: #171717;
                }

            `}</style>

            <div className="ach-pay-card-selector">
                <div className="row">
                    {[
                        { id: "Sub-Contractors", name: "Sub-Contractors", desc: "Monthly payments on a contractor candidate basis.", icon: "fa-building-o" },
                        { id: "Recruiter", name: "Recruiters", desc: "Monthly payments on a recruiter candidate basis.", icon: "fa-user" }
                    ].map((t) => {
                        const isActive = partnerType === t.id;
                        return (
                            <div className="col-md-6 mb-3" key={t.id}>
                                <div 
                                    className={`ach-pay-select-card ${isActive ? "active" : ""}`}
                                    onClick={() => handlePartnerTypeSelect(t.id)}
                                >
                                    {isActive && (
                                        <div className="ach-pay-card-check-badge">
                                            <i className="fa fa-check"></i>
                                        </div>
                                    )}
                                    <div className="ach-pay-select-card-icon">
                                        <i className={`fa ${t.icon}`}></i>
                                    </div>
                                    <div className="ach-pay-select-card-body" style={{ flexGrow: 1, minWidth: 0, paddingRight: isActive ? "14px" : "0" }}>
                                        <div className="ach-pay-select-card-title">{t.name}</div>
                                        <div className="ach-pay-select-card-desc">{t.desc}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {partnerType !== "" && (
                <div className="ach-pay-controls">
                    <div className="ach-pay-controls-group">
                        <div className="ach-pay-search-wrapper">
                            <i className="fa fa-search ach-pay-search-icon"></i>
                            <input
                                type="text"
                                placeholder="Search by invoice, period, year..."
                                className="ach-pay-search-input"
                                value={tableSearchTerm}
                                onChange={(e) => setTableSearchTerm(e.target.value)}
                                disabled={candidatePayRpt.length === 0}
                            />
                        </div>

                        <SearchableSelect
                            id="subcontractorId"
                            name="subcontractorId"
                            size="sm"
                            width="200px"
                            icon={partnerType === "Recruiter" ? "fa-handshake-o" : "fa-building"}
                            placeholder={`Select ${partnerType === "Recruiter" ? "Recruiter" : "Subcontractor"}`}
                            value={selectedSubcontractor}
                            onChange={hanldeSubcontractorChange}
                            options={subcontractors.map((item) => ({
                                value: item.fuid,
                                label: item.label,
                            }))}
                        />

                        <SearchableSelect
                            id="candidateId"
                            name="candidateId"
                            size="sm"
                            width="200px"
                            icon="fa-user"
                            placeholder="Select Candidate"
                            isDisabled={candidates.length === 0}
                            value={selectedCandidate}
                            onChange={handleCandidateChange}
                            options={candidates.map((item) => ({
                                value: item.performerid,
                                label: `${item.performername} ${item.lastName}`,
                            }))}
                        />

                        <SearchableSelect
                            id="year"
                            name="year"
                            size="sm"
                            width="140px"
                            icon="fa-calendar-o"
                            placeholder="Year"
                            value={year}
                            onChange={handleYearChange}
                            options={years.map((y) => ({
                                value: y.name,
                                label: y.name,
                            }))}
                        />

                        <button
                            type="button"
                            className="ach-pay-btn ach-pay-btn-primary"
                            disabled={!selectedCandidate || isLoading}
                            onClick={handleGenerateRpt}
                        >
                            <i className={`fa ${isLoading ? "fa-spinner fa-spin" : "fa-refresh"}`} style={{ fontSize: "11px", marginRight: "6px" }}></i>
                            {isLoading ? "Loading..." : "Generate Report"}
                        </button>
                    </div>

                    <div className="ach-pay-actions-btns">
                        {isLoading && (
                            <div className="ach-pay-loading-status">
                                <i className="fa fa-spinner fa-spin"></i>
                                <span>Loading report...</span>
                            </div>
                        )}
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={isLoading || filteredReports.length === 0}
                            onClick={subcontractorPayExportExcel}
                        >
                            <i className="fa fa-file-excel-o" style={{ color: "#171717", marginRight: "6px" }} aria-hidden="true"></i>
                            Excel Export
                        </button>
                        <button
                            type="button"
                            className="ach-pay-btn"
                            disabled={isLoading || filteredReports.length === 0}
                            onClick={subcontractorPayExportPDF}
                        >
                            <i className="fa fa-file-pdf-o" style={{ color: "#171717", marginRight: "6px" }}></i>
                            Export PDF
                        </button>
                    </div>
                </div>
            )}

            {partnerType === "" ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-info-circle"></i>
                    </div>
                    <p className="wf-empty-title">No Category Selected</p>
                    <p className="wf-empty-desc">Select Subcontractor or Recruiter category above to review candidate payment ledgers.</p>
                </div>
            ) : subcontractors.length === 0 ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-refresh fa-spin" style={{ color: "#94a3b8" }}></i>
                    </div>
                    <p className="wf-empty-title">Loading Partners...</p>
                    <p className="wf-empty-desc">Fetching available company partners from the server.</p>
                </div>
            ) : (!selectedSubcontractor || selectedSubcontractor === "0") ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-user-plus"></i>
                    </div>
                    <p className="wf-empty-title">No Partner Selected</p>
                    <p className="wf-empty-desc">Please choose a partner from the controls above to load their candidate roster.</p>
                </div>
            ) : candidates.length === 0 ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-user-times"></i>
                    </div>
                    <p className="wf-empty-title">No Candidates Found</p>
                    <p className="wf-empty-desc">No candidates are associated with the selected partner. Choose a different partner or verify their roster.</p>
                </div>
            ) : !selectedCandidate ? (
                <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                    <div className="wf-empty-icon">
                        <i className="fa fa-users"></i>
                    </div>
                    <p className="wf-empty-title">No Candidate Chosen</p>
                    <p className="wf-empty-desc">Select a specific candidate from the dropdown controls above to access payment logs.</p>
                </div>
            ) : (
                <div className="ach-pay-report-body p-0">
                    {/* KPI Metrics */}
                    {candidatePayRpt.length > 0 && (
                        <div className="ach-pay-kpi-row">
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-usd"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalInvoiced}</div>
                                    <div className="ach-pay-metric-label">Total Invoiced</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-check-circle-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalPaid}</div>
                                    <div className="ach-pay-metric-label">Total Paid</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-exclamation-triangle"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalDue}</div>
                                    <div className="ach-pay-metric-label">Outstanding Due</div>
                                </div>
                            </div>
                            <div className="ach-pay-metric-card">
                                <div className="ach-pay-metric-icon">
                                    <i className="fa fa-clock-o"></i>
                                </div>
                                <div>
                                    <div className="ach-pay-metric-val">{totalHours}h</div>
                                    <div className="ach-pay-metric-label">Total Hours</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synchronized top scrollbar */}
                    {candidatePayRpt.length > 0 && filteredReports.length > 0 && isScrollable && (
                        <div 
                            ref={topScrollRef}
                            onScroll={handleTopScroll}
                            className="ach-top-scrollbar-wrap"
                            style={{ overflowX: "auto", overflowY: "hidden", height: "8px", width: "100%", marginBottom: "2px" }}
                        >
                            <div style={{ width: `${tableWidth}px`, height: "1px" }}></div>
                        </div>
                    )}

                    <div 
                        ref={tableWrapperRef}
                        onScroll={handleTableScroll}
                        className="ach-pay-table-wrapper"
                        style={{ position: "relative" }}
                    >
                        {isLoading && <div className="wf-loading-bar" />}

                        {isLoading ? (
                            /* SKELETON TABLE LOADING SCREEN */
                            <table className="ach-pay-table">
                                <thead>
                                    <tr>
                                        <th>Year</th>
                                        <th>Period</th>
                                        <th>Hours Worked</th>
                                        <th className="text-right">Rate/hour $</th>
                                        <th className="text-right">Invoice Amount $</th>
                                        <th>Invoice #</th>
                                        <th>Paid by ACH / CHECK</th>
                                        <th className="text-right">Paid Amount $</th>
                                        <th className="text-right">Due Amount $</th>
                                        <th>Date Paid</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4, 5].map((i) => (
                                        <tr key={`skel-${i}`} className="skeleton-row">
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.07}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.05}s` }} /></td>
                                            <td><span className="skeleton-cell sk-short" style={{ animationDelay: `${i * 0.06}s` }} /></td>
                                            <td><span className="skeleton-cell sk-medium" style={{ animationDelay: `${i * 0.08}s` }} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : candidatePayRpt.length > 0 ? (
                            filteredReports.length > 0 ? (
                                <table
                                    className="ach-pay-table"
                                    id="sub_mon_pay"
                                >
                                    <thead>
                                        <tr>
                                            <th>Year</th>
                                            <th>Period</th>
                                            <th>Hours Worked</th>
                                            <th className="text-right">Rate/hour $</th>
                                            <th className="text-right">Invoice Amount $</th>
                                            <th>Invoice #</th>
                                            <th>Paid by ACH / CHECK</th>
                                            <th className="text-right">Paid Amount $</th>
                                            <th className="text-right">Due Amount $</th>
                                            <th>Date Paid</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredReports.map((item) => (
                                            <tr key={item.invoiceNo}>
                                                <td>{item.year}</td>
                                                <td>{months.find((x) => x.id === item.month)?.name}</td>
                                                <td className="col-hours" style={{ whiteSpace: "nowrap" }}>
                                                    <i className="fa fa-clock-o text-muted" style={{ marginRight: "6px", fontSize: "11.5px" }}></i>
                                                    <span style={{ fontWeight: "600", color: "#171717" }}>{item.hours}</span>
                                                    <span style={{ color: "#64748b", fontSize: "11px", marginLeft: "1px", marginRight: "5px" }}>h</span>
                                                    <span style={{ fontWeight: "600", color: "#171717" }}>{item.minutes}</span>
                                                    <span style={{ color: "#64748b", fontSize: "11px", marginLeft: "1px" }}>m</span>
                                                </td>
                                                <td className="col-rate text-right">{"$" + formatAmount(item.rate)}</td>
                                                <td className="col-amount text-right">
                                                    {item.totalAmount ? "$" + formatAmount(item.totalAmount) : ""}
                                                </td>
                                                <td>{item.invoiceNo}</td>
                                                <td>{item.paymentMode}</td>
                                                <td className="col-amount text-right">
                                                    {item.paidAmount ? "$" + formatAmount(item.paidAmount) : ""}
                                                </td>
                                                <td className="col-amount text-right">
                                                    {item.totalAmount
                                                        ? "$" + formatAmount(
                                                            (parseFloat(item.totalAmount) || 0) -
                                                            (parseFloat(item.paidAmount) || 0)
                                                        )
                                                        : ""}
                                                </td>
                                                <td>
                                                    {item.paymentDate && (
                                                        <Moment format="MMM Do, YYYY">{item.paymentDate}</Moment>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}

                                        {getSubconMonPayTotal()}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="wf-empty-state">
                                    <div className="wf-empty-icon">
                                        <i className="fa fa-search"></i>
                                    </div>
                                    <p className="wf-empty-title">No Matching Records</p>
                                    <p className="wf-empty-desc">No payment entries matched your search query. Try adjusting your search term.</p>
                                </div>
                            )
                        ) : !hasFetched ? (
                            <div className="wf-empty-state" style={{ border: "1px dashed #cbd5e1", borderRadius: "12px", background: "#f8fafc" }}>
                                <div className="wf-empty-icon">
                                    <i className="fa fa-play-circle-o" style={{ color: "#4f46e5" }}></i>
                                </div>
                                <p className="wf-empty-title">Ready to Load Data</p>
                                <p className="wf-empty-desc">Click the "Generate Report" button above to fetch candidate payments for this selection.</p>
                            </div>
                        ) : (
                            <div className="wf-empty-state">
                                <div className="wf-empty-icon">
                                    <i className="fa fa-folder-open-o"></i>
                                </div>
                                <p className="wf-empty-title">No Payments Found</p>
                                <p className="wf-empty-desc">No payment records found for this candidate selection and filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <GeneratePdf
                handlepdfShowHide={handlepdfShowHide}
                pdfShowHide={pdfShowHide}
                selectedCol={selectedCol}
                selectedColData={selectedColData}
            ></GeneratePdf>
            <GenerateExcel
                handleexcelShowHide={handleexcelShowHide}
                excelShowHide={excelShowHide}
                selectedCol={selectedCol}
                selectedColData={selectedColData}
            ></GenerateExcel>
        </div>
    );
};

export default SubcontractorCandidates;